import { Form } from 'antd';
import { CheckoutComponent } from 'components/Billing/checkout';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import routes from 'routes';
import { Currency, SubscriptionPlan, AccountType } from 'store/api/enums';
import { LicenseType } from 'store/app/enums';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import {
  billingCheckoutRequest,
  changeSubscriptionPlanRequest,
  fetchBillingDetailsRequest,
  fetchPlanPricingDetailsRequest,
  fetchSubscriptionTypesRequest,
  saveBillingDetailsRequest,
  setCurrentPlan,
  validateVATNumberRequest,
} from 'store/billing/actions';
import { BillingCheckout } from 'store/billing/models/billingCheckout';
import { BillingFormRequest } from 'store/billing/models/billingFormRequest';
import {
  getBillingDetails,
  getBillingLoading,
  getCurrentSubscription,
  getSelectedPlan,
  getSubscriptionPlanPricingDetails,
  getSubscriptionPlanTypes,
  getTaxNumberValidation,
} from 'store/billing/selectors';
import { fetchCountriesExternalDataRequest } from 'store/common/actions';
import {
  getCommonCountries,
  getCountriesExternalData,
} from 'store/common/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import {
  findSubscriptionPlanType,
  isPlanUuid,
} from 'utils/subscriptionPlanType';
import { turnMessageOn } from 'store/app/actions';
import { MessageStates } from 'containers/MessageBox';
import i18next from 'i18next';

const VAT_PERCENTAGE = 18;

const MALTA_ISO = 'MLT';

const EUROPE_ISOS = [
  'AUT', // Austria
  'BEL', // Belgium
  'BGR', // Bulgaria
  'HRV', // Croatia
  'CYP', // Cyprus
  'CZE', // Czech Republic
  'DNK', // Denmark
  'EST', // Estonia
  'FIN', // Finland
  'FRA', // France
  'DEU', // Germany
  'GRC', // Greece
  'HUN', // Hungary
  'IRL', // Ireland
  'ITA', // Italy
  'LVA', // Latvia
  'LTU', // Lithuania
  'LUX', // Luxembourg
  'NLD', // Netherlands
  'POL', // Poland
  'PRT', // Portugal
  'ROU', // Romania
  'SVK', // Slovakia
  'SVN', // Slovenia
  'ESP', // Spain
  'SWE', // Sweden
];

export const CheckoutPage: React.FC = () => {
  const parameters: any = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const querpar = new URLSearchParams(location.search);

  const changePlan = querpar.get('changePlan');
  const licenseId = parameters.licenseId ?? '';
  const selectedPlan = useSelector(getSelectedPlan);
  const countriesExternalData = useSelector(getCountriesExternalData);
  const countries = useSelector(getCommonCountries);
  const currentUser = useSelector(getAuthUser);
  const taxNumberValidation = useSelector(getTaxNumberValidation);
  const isLoading = useSelector(getBillingLoading);
  const currentSubscription = useSelector(getCurrentSubscription);
  const subscriptionPlanPricingDetails = useSelector(
    getSubscriptionPlanPricingDetails
  );
  const subscriptionPlanTypes = useSelector(getSubscriptionPlanTypes);
  const billingDetails = useSelector(getBillingDetails);
  const userLicence = useSelector(getAuthUserLicence);
  const [billingForm] = Form.useForm();
  const [hideBillingDetails, setHideBillingDetails] =
    React.useState<boolean>(false);
  const [showPage, setShowPage] = useState(false);
  const [countryISO, setCountryISO] = React.useState('');
  const [userVatNumber, setUserVatNumber] = React.useState('');
  const [vatPercentage, setVATPercentage] = React.useState(0);
  const [checkoutStarted, setCheckoutStarted] = React.useState(false);
  const [pendingBillingInfo, setPendingBillingInfo] =
    React.useState<BillingCheckout | null>(null);
  const [isEurCountry, setIsEurCountry] = React.useState(false);

  // Check if user is owner, if not redirect to main page
  React.useEffect(() => {
    if (
      currentUser &&
      AccountType[currentUser.accountType] !== AccountType.OWNER
    ) {
      navigate(routes.main);
    }
  }, [currentUser, dispatch]);

  React.useEffect(() => {
    setShowPage(true);
    currentUser &&
      checkPageLoaded(
        location.pathname,
        routes.pricing.checkout.replace(':licenseId', licenseId)
      );
    if (!billingDetails && currentUser) {
      dispatch(fetchBillingDetailsRequest(currentUser?.accountUuid));
    }
    dispatch(fetchCountriesExternalDataRequest());
    checkForTax();
  }, []);

  React.useEffect(() => {
    if (isPlanUuid(licenseId)) {
      dispatch(fetchPlanPricingDetailsRequest(licenseId));
    } else if (licenseId) {
      dispatch(fetchSubscriptionTypesRequest());
    }
  }, [licenseId]);

  React.useEffect(() => {
    if (!licenseId || isPlanUuid(licenseId)) return;
    const matched = findSubscriptionPlanType(subscriptionPlanTypes, licenseId);
    if (matched?.uuid) {
      navigate(`/pricing/checkout/${matched.uuid}`, { replace: true });
    }
  }, [licenseId, subscriptionPlanTypes, navigate]);

  React.useEffect(() => {
    if (billingDetails) {
      const countryIso = countries.find(
        (c) => c.name === billingDetails.country
      )?.iso3;

      if (countryIso) {
        setCountryISO(countryIso);
      }
      setUserVatNumber(billingDetails.vatNumber);
    }
  }, [billingDetails, countries]);

  React.useEffect(() => {
    const selectedCurrency = selectedPlan.selectedCurrency;
    if (selectedCurrency) {
      billingForm.setFieldValue('currency', selectedCurrency);
    }
  }, [selectedPlan.selectedCurrency]);

  React.useEffect(() => {
    if (changePlan || userLicence?.licenseType === LicenseType.PAID) {
      setHideBillingDetails(true);
    }
  }, [currentUser, changePlan]);

  React.useEffect(() => {
    checkForTax();
  }, [countryISO, userVatNumber, currentSubscription]);

  const checkForTax = () => {
    if (countryISO) {
      setIsEurCountry(
        EUROPE_ISOS.includes(countryISO) || countryISO === MALTA_ISO
      );
    }
    if (
      (countryISO === MALTA_ISO ||
        (EUROPE_ISOS.includes(countryISO) &&
          (userVatNumber === '' || !userVatNumber))) &&
      !currentSubscription
    ) {
      setVATPercentage(VAT_PERCENTAGE);
    } else {
      if (
        currentSubscription?.lastInvoice?.taxAmount &&
        currentSubscription?.lastInvoice?.taxAmount > 0
      ) {
        setVATPercentage(VAT_PERCENTAGE);
      } else {
        setVATPercentage(0);
      }
    }
  };

  React.useEffect(() => {
    if (
      taxNumberValidation &&
      checkoutStarted &&
      pendingBillingInfo &&
      pendingBillingInfo.vatNumber === taxNumberValidation.taxNumber
    ) {
      if (taxNumberValidation.isValid) {
        let billing = {
          ...pendingBillingInfo,
          accountId: currentUser?.accountUuid,
        };
        billing = { ...billing, typeId: licenseId };
        dispatch(billingCheckoutRequest(billing));
      } else {
        dispatch(
          turnMessageOn({
            title: i18next.t('billing.vat-validation-failed-title'),
            description: i18next.t('billing.vat-validation-failed-description'),
            type: MessageStates.ERROR,
          })
        );
      }
      setCheckoutStarted(false);
      setPendingBillingInfo(null);
    }
  }, [
    taxNumberValidation,
    checkoutStarted,
    pendingBillingInfo,
    currentUser,
    licenseId,
    dispatch,
  ]);

  const backToChangePlan = () => {
    navigate(routes.pricing.root);
  };

  const changeCurrency = (newCurrency: Currency) => {
    dispatch(
      setCurrentPlan(selectedPlan.currentPlan, newCurrency, selectedPlan.price)
    );
  };

  const continueToCheckout = (billing: BillingCheckout) => {
    const trimmedBilling = {
      additionalEntities: billing.additionalEntities,
      vatnumberchecker: billing.vatnumberchecker,
    } as BillingCheckout;
    if (hideBillingDetails || userLicence?.licenseType === LicenseType.PAID) {
      dispatch(changeSubscriptionPlanRequest(licenseId, trimmedBilling));
    } else {
      const countryCode = countries.find(
        (c) => c.name === billing.country
      )?.iso3;

      const requiresVatValidation =
        countryCode &&
        billing.vatNumber &&
        (countryCode === MALTA_ISO || EUROPE_ISOS.includes(countryCode));

      if (requiresVatValidation) {
        setPendingBillingInfo(billing);
        setCheckoutStarted(true);
        dispatch(validateVATNumberRequest(countryCode, billing.vatNumber));
      } else {
        let enhancedBilling = {
          ...billing,
          accountId: currentUser?.accountUuid,
        };
        enhancedBilling = { ...enhancedBilling, typeId: licenseId };
        dispatch(billingCheckoutRequest(enhancedBilling));
      }
    }
  };

  const saveBillingDetails = (billingDetails: BillingFormRequest) => {
    dispatch(
      saveBillingDetailsRequest(currentUser?.accountUuid ?? '', billingDetails)
    );
  };

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${i18next.t('navigation.checkout')} | ${process.env.PROJECT_NAME}`}</title>
            <meta
              name="description"
              content={i18next.t('billing.meta-description-checkout')}
            />
          </Helmet>
          <CheckoutComponent
            selectedPlan={selectedPlan}
            backToChangePlan={backToChangePlan}
            continueToCheckout={continueToCheckout}
            countriesExternalData={countriesExternalData}
            countries={countries}
            changeCurrency={changeCurrency}
            isLoading={isLoading}
            subscriptionPlanPricingDetails={subscriptionPlanPricingDetails}
            hideBillingDetails={hideBillingDetails}
            billingDetails={billingDetails}
            saveBillingDetails={saveBillingDetails}
            countryISO={countryISO}
            userVatNumber={userVatNumber}
            setCountryISO={setCountryISO}
            setUserVatNumber={setUserVatNumber}
            billingForm={billingForm}
            vatPercentage={vatPercentage}
            setVATPercentage={setVATPercentage}
            isEurCountry={isEurCountry}
          />
        </>
      )}
    </React.Fragment>
  );
};
